<script>
import { defineComponent } from 'vue';
import DynamicDataTable from '@/components/DynamicDataTable.vue';
import { mapGetters } from 'vuex';
import InputRulesMixin from '@/Mixins/InputRulesMixin';
import FileHandlerService from '@/services/file-handler';
import DeleteDialog from '@/components/DeleteDialog.vue';
import { getColumns } from '@/services/api/column.api';
import {
  createRecord,
  deleteRecord,
  updateRecord,
} from '@/services/api/record.api';
import { queryTablesV2 } from '@/services/api/v2/connect_v2';

export default defineComponent({
  name: 'DHMEManualImportDrawings',
  components: {
    DeleteDialog,
    DynamicDataTable,
  },
  mixins: [InputRulesMixin],
  data: () => {
    return {
      manualImportDrawingHeaders: [
        {
          text: 'Title',
          value: 'title',
          input: true,
          hasSlot: true,
          type: 'text',
          width: '400px',
        },
        {
          text: 'Drawing',
          value: 'drawing',
          type: 'text',
        },
        { text: 'Actions', value: 'actions', align: 'right', sortable: false },
      ],
      manualImportDrawingRecords: [],
      manualImportDrawingColumns: [],
      manualImportDrawingTableId: null,
      drawingToDelete: null,
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters(['project']),
  },
  mounted() {
    this.fetchDrawings();
  },
  methods: {
    async uploadDrawings(event) {
      this.isLoading = true;
      const files = Array.from(event.target.files);

      for (const file of files) {
        const drawing = await FileHandlerService.handleFile(file);

        const uploadedFile = await createRecord({
          project: { id: this.project.id },
          table: { id: this.manualImportDrawingTableId },
          record: {
            title: drawing.name,
            drawing: drawing,
          },
        });

        this.manualImportDrawingRecords.push(uploadedFile);
      }

      this.isLoading = false;
      event.target.value = ''; // Reset the file input
    },
    async fetchDrawings() {
      this.isLoading = true;
      let { drawings } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_DRAWINGS',
            project: this.project.id,
            as: 'drawings',
            columns: [
              {
                name: 'title',
              },
              {
                name: 'drawing',
              },
            ],
          },
        ],
      });

      this.manualImportDrawingRecords = drawings.records;
      this.manualImportDrawingTableId = drawings.id;
      await this.fetchColumns();
      this.isLoading = false;
    },
    async deleteDrawing() {
      this.isLoading = true;
      await deleteRecord(this.drawingToDelete.id, {
        project: {
          id: this.project.id,
        },
        table: {
          id: this.manualImportDrawingTableId,
        },
      });

      this.manualImportDrawingRecords = this.manualImportDrawingRecords.filter(
        (x) => x.id !== this.drawingToDelete.id
      );
      this.drawingToDelete = null;
      this.isLoading = false;
    },
    async fetchColumns() {
      this.manualImportDrawingColumns = await getColumns(
        this.project.id,
        this.manualImportDrawingTableId
      );
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    setupDelete(drawing) {
      this.drawingToDelete = drawing;
    },
    async updateRecord(recordId, column, value) {
      this.isLoading = true;
      let body = {};
      body[column] = value;

      let updatedRecord = await updateRecord(recordId, {
        project: { id: this.project.id },
        table: { id: this.manualImportDrawingTableId },
        record: body,
      });

      Object.assign(
        this.manualImportDrawingRecords.find((x) => x.id === recordId),
        updatedRecord
      );

      this.isLoading = false;
    },
  },
});
</script>

<template>
  <dynamic-data-table
    :project-id="project.id"
    :table-columns="manualImportDrawingColumns"
    :table-headers="manualImportDrawingHeaders"
    :table-id="manualImportDrawingTableId"
    :table-records="manualImportDrawingRecords"
    auto-sort-column="object_type"
    can-delete
    class="flex-grow-1 ant-glass-background radius-0"
    export-file-name="manual_import_module_export"
    has-options
    table-title="Drawings"
    @deleteItem="setupDelete"
  >
    <template
      v-for="header in manualImportDrawingHeaders.filter((x) => x.input)"
      #[`item.${header.value}`]="{ value, rowId, item }"
    >
      <td :key="header.value" class="ant-border-left">
        <v-text-field
          :suffix="header.suffix"
          :type="header.type"
          :value="value"
          clearable
          dense
          filled
          hide-details
          single-line
          @change="updateRecord(item.id, header.value, $event)"
        />
      </td>
    </template>
    <template #table-actions>
      <delete-dialog
        :dialog="drawingToDelete !== null"
        :title="`Are you sure you want to delete drawing ${drawingToDelete?.title}`"
        @closeDialog="drawingToDelete = null"
        @deleteAction="deleteDrawing"
      />
      <v-btn
        :disabled="isLoading"
        :loading="isLoading"
        color="primary"
        elevation="0"
        small
        @click="triggerFileInput"
      >
        <v-icon>mdi-plus</v-icon>
        Add
      </v-btn>
      <input
        ref="fileInput"
        accept="application/pdf"
        multiple
        style="display: none"
        type="file"
        @change="uploadDrawings"
      />
    </template>
  </dynamic-data-table>
</template>

<style lang="scss" scoped>
td {
  padding: 0 !important;
}

::v-deep .v-text-field__details {
  height: 0;
  overflow: hidden;
  min-height: 0;
}

::v-deep .x-small-text-field {
  max-width: 100px !important;
}

::v-deep .small-text-field {
  max-width: 150px !important;
}

::v-deep .normal-text-field {
  max-width: 200px !important;
}
</style>
